import { useTranslation } from 'react-i18next'
import { getMediaUrl } from '@/utils/media'
import { motion, useScroll, useTransform } from 'framer-motion'
import { useRef, useState, useEffect } from 'react'
import {
  Shield,
  Zap,
  Clock,
  Award,
  ArrowRight,
  CheckCircle2,
  Building2,
  Ship,
  Factory,
  Droplets,
  Cable,
  ChevronRight,
} from 'lucide-react'
import { SEO } from '@/components/SEO'
import { Button } from '@/components/ui/Button'
import { LocalizedLink } from '@/components/LocalizedLink'
import { DatasheetDownloadModal } from '@/components/DatasheetDownloadModal'

// Animated counter hook
function useCounter(end: number, duration: number = 2000, startOnView: boolean = true) {
  const [count, setCount] = useState(0)
  const [hasStarted, setHasStarted] = useState(!startOnView)
  const ref = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!hasStarted) return
    let startTime: number
    let animationFrame: number
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp
      const progress = Math.min((timestamp - startTime) / duration, 1)
      setCount(Math.floor(progress * end))
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate)
      }
    }
    animationFrame = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(animationFrame)
  }, [end, duration, hasStarted])

  useEffect(() => {
    if (!startOnView || !ref.current) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasStarted(true)
          observer.disconnect()
        }
      },
      { threshold: 0.5 }
    )
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [startOnView])

  return { count, ref }
}

// Feature card component
function FeatureCard({
  icon: Icon,
  title,
  description,
  delay,
}: {
  icon: React.ElementType
  title: string
  description: string
  delay: number
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      whileHover={{ y: -8, transition: { duration: 0.3 } }}
      className="relative p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl overflow-hidden w-full sm:w-auto sm:flex-1 sm:max-w-[300px]"
      style={{
        background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)',
        backdropFilter: 'blur(8px)',
        border: '1px solid rgba(255,255,255,0.1)',
      }}
    >
      {/* Gradient border effect */}
      <div
        className="absolute inset-0 rounded-xl sm:rounded-2xl p-px opacity-50"
        style={{
          background: 'linear-gradient(135deg, #00a3cc 0%, #003b7a 50%, #00a3cc 100%)',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
        }}
      />

      <div
        className="w-12 h-12 sm:w-[60px] sm:h-[60px] rounded-lg sm:rounded-xl flex items-center justify-center mb-4 sm:mb-6"
        style={{
          background: 'linear-gradient(135deg, #00a3cc 0%, #0077b3 100%)',
          boxShadow: '0 10px 30px rgba(0, 163, 204, 0.3)',
        }}
      >
        <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
      </div>

      <h3 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">
        {title}
      </h3>

      <p className="text-sm sm:text-base text-white/70 leading-relaxed">
        {description}
      </p>
    </motion.div>
  )
}

// Application card component
function ApplicationCard({
  icon: Icon,
  title,
  delay,
}: {
  icon: React.ElementType
  title: string
  delay: number
}) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay }}
      whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        padding: '1.25rem 1.5rem',
        borderRadius: '1rem',
        backgroundColor: '#ffffff',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        cursor: 'default',
      }}
    >
      <div
        style={{
          width: '48px',
          height: '48px',
          borderRadius: '0.75rem',
          background: 'linear-gradient(135deg, #003b7a 0%, #001f42 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon style={{ width: '24px', height: '24px', color: '#00a3cc' }} />
      </div>
      <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.95rem' }}>{title}</span>
    </motion.div>
  )
}

export function CathodicProtectionPage() {
  const { t } = useTranslation()
  const heroRef = useRef<HTMLDivElement>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const openModal = () => setIsModalOpen(true)

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  })

  const heroY = useTransform(scrollYProgress, [0, 1], [0, 200])
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0])

  const stats = [
    { value: 18, suffix: '+', label: t('cathodicProtection.stat1Label') },
    { value: 55, suffix: '', label: t('cathodicProtection.stat2Label') },
    { value: 100, suffix: '+', label: t('cathodicProtection.stat3Label') },
    { value: 20, suffix: '+', label: t('cathodicProtection.stat4Label') },
  ]

  const features = [
    {
      icon: Clock,
      title: t('cathodicProtection.feature1Title'),
      description: t('cathodicProtection.feature1Text'),
    },
    {
      icon: Zap,
      title: t('cathodicProtection.feature2Title'),
      description: t('cathodicProtection.feature2Text'),
    },
    {
      icon: Shield,
      title: t('cathodicProtection.feature3Title'),
      description: t('cathodicProtection.feature3Text'),
    },
    {
      icon: Award,
      title: t('cathodicProtection.feature4Title'),
      description: t('cathodicProtection.feature4Text'),
    },
  ]

  const applications = [
    { icon: Building2, title: t('cathodicProtection.application1') },
    { icon: Cable, title: t('cathodicProtection.application2') },
    { icon: Ship, title: t('cathodicProtection.application3') },
    { icon: Droplets, title: t('cathodicProtection.application4') },
    { icon: Factory, title: t('cathodicProtection.application5') },
    { icon: Cable, title: t('cathodicProtection.application6') },
  ]

  return (
    <>
      <SEO
        title={t('cathodicProtection.metaTitle')}
        description={t('cathodicProtection.metaDescription')}
        image={getMediaUrl('products/cathodic-protection/fotoCP.jpg')}
      />

      {/* ============ EPIC HERO SECTION ============ */}
      <section
        ref={heroRef}
        style={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          marginTop: '-80px',
          paddingTop: '80px',
        }}
      >
        {/* Background Image with Parallax */}
        <motion.div
          style={{
            position: 'absolute',
            inset: 0,
            y: heroY,
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url(${getMediaUrl('foto/CP_top.JPG')})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              transform: 'scale(1.1)',
            }}
          />
        </motion.div>

        {/* Gradient Overlay */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, rgba(0,31,66,0.88) 0%, rgba(0,59,122,0.75) 50%, rgba(0,31,66,0.82) 100%)`,
          }}
        />

        {/* Animated Grid Pattern */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `linear-gradient(rgba(0,163,204,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,163,204,0.03) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}
        />

        {/* Floating Orbs */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            position: 'absolute',
            top: '20%',
            right: '10%',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(0,163,204,0.2) 0%, transparent 70%)',
            filter: 'blur(40px)',
          }}
        />
        <motion.div
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            position: 'absolute',
            bottom: '10%',
            left: '5%',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(0,59,122,0.3) 0%, transparent 70%)',
            filter: 'blur(40px)',
          }}
        />

        {/* Hero Content */}
        <motion.div
          style={{ opacity: heroOpacity }}
          className="container-main relative z-10"
        >
          <div style={{ maxWidth: '900px' }}>
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                borderRadius: '2rem',
                backgroundColor: 'rgba(0,163,204,0.2)',
                border: '1px solid rgba(0,163,204,0.3)',
                marginBottom: '1.5rem',
              }}
            >
              <Shield style={{ width: '16px', height: '16px', color: '#00a3cc' }} />
              <span
                style={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#00a3cc',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {t('cathodicProtection.pretitle')}
              </span>
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              style={{
                fontSize: 'clamp(2.5rem, 6vw, 4rem)',
                fontWeight: 800,
                color: '#ffffff',
                lineHeight: 1.1,
                marginBottom: '1.5rem',
              }}
            >
              {t('cathodicProtection.heroTitle')}
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              style={{
                fontSize: '1.25rem',
                color: 'rgba(255,255,255,0.8)',
                lineHeight: 1.6,
                maxWidth: '700px',
                marginBottom: '2.5rem',
              }}
            >
              {t('cathodicProtection.heroSubtitle')}
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}
            >
              <LocalizedLink to="/contact">
                <Button variant="primary" size="lg">
                  {t('buttons.requestQuote')}
                  <ArrowRight style={{ marginLeft: '0.5rem', width: '20px', height: '20px' }} />
                </Button>
              </LocalizedLink>
              <Button
                variant="outline"
                size="lg"
                style={{
                  borderColor: 'rgba(255,255,255,0.3)',
                  color: '#ffffff',
                }}
                onClick={openModal}
              >
                {t('buttons.downloadDatasheet')}
              </Button>
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
          style={{
            position: 'absolute',
            bottom: '2rem',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <span
            style={{
              fontSize: '0.75rem',
              color: 'rgba(255,255,255,0.5)',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
            }}
          >
            Scroll
          </span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
            style={{
              width: '24px',
              height: '40px',
              borderRadius: '12px',
              border: '2px solid rgba(255,255,255,0.3)',
              display: 'flex',
              justifyContent: 'center',
              paddingTop: '8px',
            }}
          >
            <motion.div
              style={{
                width: '4px',
                height: '8px',
                borderRadius: '2px',
                backgroundColor: '#00a3cc',
              }}
            />
          </motion.div>
        </motion.div>
      </section>

      {/* ============ STATS SECTION ============ */}
      <section
        style={{
          position: 'relative',
          padding: '5rem 0',
          background: 'linear-gradient(135deg, #001f42 0%, #003b7a 100%)',
          overflow: 'hidden',
        }}
      >
        {/* Decorative Elements */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent 0%, #00a3cc 50%, transparent 100%)',
          }}
        />

        <div className="container-main">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '3rem',
              textAlign: 'center',
            }}
          >
            {stats.map((stat, index) => {
              const { count, ref } = useCounter(stat.value)
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <span
                    ref={ref}
                    style={{
                      fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
                      fontWeight: 800,
                      background: 'linear-gradient(135deg, #00a3cc 0%, #ffffff 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {count.toLocaleString()}
                    {stat.suffix}
                  </span>
                  <p
                    style={{
                      marginTop: '0.5rem',
                      fontSize: '1rem',
                      color: 'rgba(255,255,255,0.7)',
                      fontWeight: 500,
                    }}
                  >
                    {stat.label}
                  </p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ============ OVERVIEW SECTION ============ */}
      <section style={{ padding: '6rem 0', backgroundColor: '#ffffff' }}>
        <div className="container-main">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))',
              gap: '4rem',
              alignItems: 'center',
            }}
          >
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span
                style={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#00a3cc',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                }}
              >
                {t('cathodicProtection.technologyLabel')}
              </span>
              <h2
                style={{
                  fontSize: 'clamp(2rem, 4vw, 2.5rem)',
                  fontWeight: 800,
                  color: '#0f172a',
                  marginTop: '0.5rem',
                  marginBottom: '1.5rem',
                  lineHeight: 1.2,
                }}
              >
                {t('cathodicProtection.overviewTitle')}
              </h2>
              <p
                style={{
                  fontSize: '1.125rem',
                  color: '#64748b',
                  lineHeight: 1.7,
                  marginBottom: '1.5rem',
                }}
              >
                {t('cathodicProtection.overviewText')}
              </p>
              <p
                style={{
                  fontSize: '1rem',
                  color: '#64748b',
                  lineHeight: 1.7,
                  marginBottom: '2rem',
                }}
              >
                {t('cathodicProtection.overviewText2')}
              </p>

              {/* AMPP Badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '1rem 1.5rem',
                  borderRadius: '1rem',
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                }}
              >
                <img
                  src={getMediaUrl('company/certifications/ampp-membership-badge.jpeg')}
                  alt="AMPP Member"
                  style={{ height: '60px', width: 'auto' }}
                />
                <div>
                  <p style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.95rem' }}>
                    AMPP Member
                  </p>
                  <p style={{ fontSize: '0.85rem', color: '#64748b' }}>
                    Association for Materials Protection and Performance
                  </p>
                </div>
              </motion.div>
            </motion.div>

            {/* Visual Element */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              style={{ position: 'relative' }}
            >
              <div
                style={{
                  position: 'relative',
                  borderRadius: '1.5rem',
                  overflow: 'hidden',
                  boxShadow: '0 25px 50px -12px rgba(0,0,0,0.15)',
                }}
              >
                <img
                  src={getMediaUrl('foto/CP_center.jpg')}
                  alt="MMO Anodes Installation"
                  className="w-full aspect-[4/3] sm:aspect-[16/10] object-cover"
                />
                {/* Overlay gradient */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(to top, rgba(0,31,66,0.6) 0%, transparent 50%)',
                  }}
                />
              </div>

              {/* Floating badge - hidden on mobile */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="hidden sm:block absolute -bottom-5 -right-5 p-4 sm:p-6 rounded-xl"
                style={{
                  backgroundColor: '#003b7a',
                  boxShadow: '0 20px 40px rgba(0,59,122,0.3)',
                }}
              >
                <p className="text-white text-xs font-semibold">
                  {t('cathodicProtection.certified')}
                </p>
                <p className="text-white text-xl sm:text-2xl font-extrabold">
                  ISO 9001
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ FEATURES SECTION ============ */}
      <section
        style={{
          padding: '6rem 0',
          background: 'linear-gradient(180deg, #001f42 0%, #003b7a 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background decoration */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '800px',
            height: '800px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(0,163,204,0.1) 0%, transparent 70%)',
            filter: 'blur(60px)',
          }}
        />

        <div className="container-main relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            style={{ textAlign: 'center', marginBottom: '4rem' }}
          >
            <span
              style={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#00a3cc',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}
            >
              {t('cathodicProtection.whyChooseUs')}
            </span>
            <h2
              style={{
                fontSize: 'clamp(2rem, 4vw, 2.75rem)',
                fontWeight: 800,
                color: '#ffffff',
                marginTop: '0.5rem',
              }}
            >
              {t('cathodicProtection.featuresTitle')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                delay={index * 0.1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ============ APPLICATIONS SECTION ============ */}
      <section style={{ padding: '6rem 0', backgroundColor: '#f8fafc' }}>
        <div className="container-main">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))',
              gap: '4rem',
              alignItems: 'start',
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span
                style={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#00a3cc',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                }}
              >
                {t('cathodicProtection.applicationSectors')}
              </span>
              <h2
                style={{
                  fontSize: 'clamp(2rem, 4vw, 2.5rem)',
                  fontWeight: 800,
                  color: '#0f172a',
                  marginTop: '0.5rem',
                  marginBottom: '1.5rem',
                }}
              >
                {t('cathodicProtection.applicationsTitle')}
              </h2>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '1rem',
                }}
              >
                {applications.map((app, index) => (
                  <ApplicationCard
                    key={index}
                    icon={app.icon}
                    title={app.title}
                    delay={index * 0.05}
                  />
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div
                style={{
                  padding: '2.5rem',
                  borderRadius: '1.5rem',
                  background: 'linear-gradient(135deg, #003b7a 0%, #001f42 100%)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Decorative circle */}
                <div
                  style={{
                    position: 'absolute',
                    top: '-50px',
                    right: '-50px',
                    width: '200px',
                    height: '200px',
                    borderRadius: '50%',
                    background: 'rgba(0,163,204,0.1)',
                  }}
                />

                <h3
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    color: '#ffffff',
                    marginBottom: '1rem',
                  }}
                >
                  {t('cathodicProtection.iccpTitle')}
                </h3>

                <p
                  style={{
                    fontSize: '1rem',
                    color: 'rgba(255,255,255,0.8)',
                    lineHeight: 1.7,
                    marginBottom: '1.5rem',
                  }}
                >
                  {t('cathodicProtection.iccpText')}
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {[t('cathodicProtection.iccpBenefit1'), t('cathodicProtection.iccpBenefit2'), t('cathodicProtection.iccpBenefit3')].map(
                    (item, index) => (
                      <div
                        key={index}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}
                      >
                        <CheckCircle2
                          style={{ width: '20px', height: '20px', color: '#00a3cc', flexShrink: 0 }}
                        />
                        <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.95rem' }}>
                          {item}
                        </span>
                      </div>
                    )
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ CTA SECTION ============ */}
      <section
        style={{
          position: 'relative',
          padding: '6rem 0',
          background: 'linear-gradient(135deg, #003b7a 0%, #001f42 50%, #003b7a 100%)',
          overflow: 'hidden',
        }}
      >
        {/* Animated background elements */}
        <motion.div
          animate={{
            rotate: [0, 360],
          }}
          transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
          style={{
            position: 'absolute',
            top: '-200px',
            right: '-200px',
            width: '600px',
            height: '600px',
            borderRadius: '50%',
            border: '1px solid rgba(0,163,204,0.1)',
          }}
        />
        <motion.div
          animate={{
            rotate: [360, 0],
          }}
          transition={{ duration: 45, repeat: Infinity, ease: 'linear' }}
          style={{
            position: 'absolute',
            bottom: '-150px',
            left: '-150px',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            border: '1px solid rgba(0,163,204,0.1)',
          }}
        />

        <div className="container-main relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}
          >
            <span
              style={{
                display: 'inline-block',
                padding: '0.5rem 1.5rem',
                borderRadius: '2rem',
                backgroundColor: 'rgba(0,163,204,0.2)',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#00a3cc',
                marginBottom: '1.5rem',
              }}
            >
              {t('cathodicProtection.startProject')}
            </span>

            <h2
              style={{
                fontSize: 'clamp(2rem, 5vw, 3rem)',
                fontWeight: 800,
                color: '#ffffff',
                marginBottom: '1.5rem',
                lineHeight: 1.2,
              }}
            >
              {t('cathodicProtection.ctaTitle')}
            </h2>

            <p
              style={{
                fontSize: '1.25rem',
                color: 'rgba(255,255,255,0.7)',
                marginBottom: '2.5rem',
                lineHeight: 1.6,
              }}
            >
              {t('cathodicProtection.ctaText')}
            </p>

            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '1rem',
                justifyContent: 'center',
              }}
            >
              <LocalizedLink to="/contact">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                  <Button variant="primary" size="lg">
                    {t('buttons.requestQuote')}
                    <ChevronRight style={{ marginLeft: '0.5rem', width: '20px', height: '20px' }} />
                  </Button>
                </motion.div>
              </LocalizedLink>

              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                <Button
                  variant="outline"
                  size="lg"
                  style={{
                    borderColor: 'rgba(255,255,255,0.3)',
                    color: '#ffffff',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                  }}
                  onClick={openModal}
                >
                  {t('buttons.downloadDatasheet')}
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      <DatasheetDownloadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        product="cathodic-protection"
        productLabel={t('cathodicProtection.heroTitle')}
        fileUrl={getMediaUrl('documents/cathodic-protection-datasheets.zip')}
        fileName="cathodic-protection-datasheets.zip"
      />
    </>
  )
}
